/**
 * Structure (site) identity card — name, description, location, weather,
 * and ownership/sharing summary on the site dashboard rail.
 *
 * Reads site identity and weather from the new odl-app `site` contract prop
 * (plain JS, sourced from the siteDashboard slice via the view). The
 * deactivate dialog continues to use the legacy selectedStructure Immutable
 * Map for its mutation flow, which is outside the new contract.
 *
 * Contains:
 * - StructureCard — dashboard rail card (default export, connect-wrapped)
 */
import { CloudTwoTone } from '@mui/icons-material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Map } from 'immutable';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { patchSite } from '@sophocles/api/odlApp';
import BasicDashboardCard from '@sophocles/components/BasicDashboardCard';
import StatusChip from '@sophocles/components/StatusChip';
import { getAuthUserSelector, getSelectedCustomerSelector } from '@sophocles/features/auth/authSelectors';
import { selectCapabilities } from '@sophocles/features/capabilities';
import { selectSitePermissions } from '@sophocles/features/entities/site';
import { getEntityDataSelector } from '@sophocles/features/resources/resourceHandlerSelectors';
import { getSelectedPlantSelector, selectedPlant } from '@sophocles/features/sites/slices/selectedPlant/selectors';
import { putCustomExtraFieldsInStore } from '@sophocles/features/sites/slices/selectedStructure/actions';
import {
  extraStructureFields,
  getSelectedStructure,
  selectedStructure,
} from '@sophocles/features/sites/slices/selectedStructure/selectors';
import { selectTenantInfo } from '@sophocles/features/tenant/slice';
import { addToast } from '@sophocles/features/toast';
import { unshareVirtualAccess } from '@sophocles/features/virtual/api';
import { withRouter } from '@sophocles/router/hoc';
import { userPermCheck } from '@sophocles/utils/userPermissionUtils';
import DeactivateSiteDialog from '@sophocles/views/sites/components/StructureCard/DeactivateSiteDialog';
import StructureDetailsList from '@sophocles/views/sites/components/StructureCard/StructureDetailsList';
import { resolveDeleteSiteLabel } from '@sophocles/views/sites/utils/deleteSiteLabel';

/**
 * Convert a Kelvin temperature to Fahrenheit, rounded to the nearest integer.
 * Returns null when the input is null/undefined (no reading available).
 *
 * @param {number|null|undefined} kelvin
 * @returns {number|null}
 */
function kelvinToFahrenheit(kelvin) {
  if (kelvin == null) return null;
  return Math.round(((kelvin - 273.15) * 9) / 5 + 32);
}

function mapStateToProps(state, ownProps) {
  // siteId for virtualMemberships comes from the plain-JS snapshot prop the
  // view already passes down — avoids a dependency on getSelectedStructure for
  // this lookup.
  const siteId = ownProps.site?.id;
  return {
    // capabilities replaces getAuthUserSelector for is_staff / permission gates.
    capabilities: selectCapabilities(state),
    // user retained for DeactivateSiteDialog's legacy write path (userPermCheck
    // pliny.delete_site).
    user: getAuthUserSelector(state),
    customer: getSelectedCustomerSelector(state),
    // sitePermissions carries the server-computed Cloud-chip access decision
    // (can_access_primary_site) — the FE can't see cross-customer
    // memberships/reseller relationships, so this is never re-derived here.
    sitePermissions: selectSitePermissions(siteId)(state),
    // extraFields sourced from site.extra_fields plain dict (ownProps.site),
    // passed through to StructureDetailsList for value rendering.
    // fieldVisibility drives the per-field show toggle in StructureDetailsList;
    // the user configures it via StructureCardConfigureDialog.
    fieldVisibility: extraStructureFields(state),
    tenantInfo: selectTenantInfo(state),
    selectedPlant: getSelectedPlantSelector(state),
    selectedPlant_id: selectedPlant(state),
    selectedStructure_id: selectedStructure(state),
    // legacySiteEntity: retained for DeactivateSiteDialog and the
    // handleUpdateLocation fallback, which require the legacy Immutable Map shape.
    legacySiteEntity: getSelectedStructure(state),
    entityData: getEntityDataSelector(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    patchSite: (siteId, values) => patchSite(siteId, values),
    putCustomExtraFieldsInStore: fields => dispatch(putCustomExtraFieldsInStore(fields)),
    addToast: toast => dispatch(addToast(toast)),
    unshareVirtualAccess: data => dispatch(unshareVirtualAccess(data)),
  };
}

/**
 * Extract bare custom_data keys from the snapshot extra_fields map.
 * extra_fields keys for user fields are prefixed 'user_'; strip the prefix
 * to recover the original custom_data key names that putCustomExtraFieldsInStore
 * expects.
 *
 * @param {object|null|undefined} extraFields - site.extra_fields from the snapshot
 * @returns {string[]}
 */
function getCustomDataKeys(extraFields) {
  if (!extraFields) return [];
  return Object.keys(extraFields)
    .filter(k => k.startsWith('user_'))
    .map(k => k.slice('user_'.length));
}

class StructureCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      deleteOpen: false,
    };
  }

  componentDidMount() {
    const customKeys = getCustomDataKeys(this.props.site?.extra_fields);
    if (customKeys.length > 0) {
      this.props.putCustomExtraFieldsInStore(customKeys);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.site?.id !== this.props.site?.id) {
      const customKeys = getCustomDataKeys(this.props.site?.extra_fields);
      if (customKeys.length > 0) {
        this.props.putCustomExtraFieldsInStore(customKeys);
      }
    }
  }

  openDeleteDialog = () => this.setState({ deleteOpen: true });

  closeDeleteDialog = () => this.setState({ deleteOpen: false });

  handleUpdateLocation = (latitude, longitude) => {
    const { patchSite, site, legacySiteEntity } = this.props;
    patchSite(site?.id ?? legacySiteEntity.get('id'), { latitude, longitude });
  };

  render() {
    const {
      site,
      legacySiteEntity,
      selectedPlant,
      customer,
      isBillboard,
      capabilities,
      user,
      router,
      params,
      t,
      weatherConditions,
      loading,
      fieldVisibility,
      status,
      sitePermissions,
    } = this.props;
    // is_staff from capabilities slice (new contract).
    const isStaff = capabilities?.is_staff ?? false;
    // Extra fields sourced from the site snapshot — plain dict from selectSiteData.
    const extraFields = site?.extra_fields ?? {};

    const customerPreferences = customer.get('preferences', Map());

    const siteSchema = customerPreferences.get('site_schema', {});
    const name = site?.name ?? '';
    const description = site?.description ?? '';

    const isVirtual = selectedPlant.get('is_virtual', false);

    // Weather is present when the new site contract supplies a weather object.
    // Temperature in the contract is raw Kelvin — convert for display.
    const weatherFeatureEnabled = weatherConditions != null;
    const weatherTempF = kelvinToFahrenheit(weatherConditions?.temperature);
    // Build a description string that includes temperature (°F) and condition
    // for the StructureDetailsList tooltip. Temperature is raw Kelvin in the
    // new contract — convert before display.
    const weatherDescription =
      weatherConditions != null
        ? [weatherTempF != null ? `${weatherTempF}°F` : null, weatherConditions.condition].filter(Boolean).join(' · ')
        : null;
    const weatherDisplayConditions =
      weatherConditions != null
        ? {
            ...weatherConditions,
            // `description` carries the human-readable summary for the tooltip.
            // darkness_score passes through from the contract when present;
            // the FE renders null-safely so it works before the BE deploys.
            description: weatherDescription,
          }
        : null;

    // Shared chip: site.shared_with (non-primary memberships) from the new contract.
    const sharedWith = site?.shared_with ?? null;
    const isShared = (sharedWith?.length ?? 0) > 0;

    // Cloud chip (virtual site) navigation uses the snapshot owner shape:
    //   site?.owner?.customer?.id  — customer id for URL
    //   site?.owner?.id            — site group id for URL
    //   site?.id                   — structure id for URL
    // 'no primary' gate triggers when owner has no customer.
    const siteOwner = site?.owner ?? null;
    // Backend-computed (meta.permissions.can_access_primary_site) — the FE
    // can't see cross-customer memberships/reseller relationships, so this
    // is never re-derived client-side. Absent flag reads as no access.
    const hasAccessToPrimary = sitePermissions?.can_access_primary_site ?? false;
    const primarySitePath = `/c/${siteOwner?.customer?.id}/plant/${siteOwner?.id}/structure/${site?.id}`;

    const deleteSiteLabel = resolveDeleteSiteLabel(t, { isVirtual, isBillboard });
    const structureDashboardPath = `/c/${params.cust_id}/plant/${params.sitegroup_id}/structure/${params.site_id}`;

    return (
      <BasicDashboardCard
        loading={loading}
        status={status}
        title={name || (isBillboard ? t('sites.structureDetails') : t('sites.siteDetails'))}
        subtitle={description || null}
        toolbarExtras={[
          site?.deactivated && (
            <StatusChip
              key="deactivated"
              color="neutral"
              label={t('general.deactivated')}
              sx={{ mr: 'var(--space-1)' }}
            />
          ),
          // Cloud (primary site, shared) — clicking goes straight to the
          // site settings screen's Sharing section, staff-only to match that
          // section's own gating.
          !isVirtual && isStaff && isShared && (
            <Tooltip key="cloud-shared" title={t('sites.sharedSiteTooltip')}>
              <IconButton
                size="small"
                aria-label={t('sites.sharedSiteTooltip')}
                onClick={() => router.navigate(`${structureDashboardPath}/settings/third-party`)}
              >
                <CloudTwoTone fontSize="small" sx={{ color: 'info.main' }} />
              </IconButton>
            </Tooltip>
          ),
          // Cloud (virtual site) — links to the primary site's dashboard
          // when the current user has access to it; a broken (no-primary)
          // link stays staff-only since it's a data-integrity signal, not a
          // user-actionable one. Opens in a new tab so navigating away from a
          // virtual site's own dashboard never triggers its refresh-state bug.
          // Without access the cloud is an indicator only, so it is muted and
          // carries no affordance.
          isVirtual &&
            (siteOwner?.customer?.id == null ? (
              isStaff && (
                <Tooltip key="no-primary-warning" title={t('sites.noPrimaryAssociationTooltip')}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', p: 0.5 }}>
                    <CloudTwoTone fontSize="small" sx={{ color: 'error.main' }} />
                  </Box>
                </Tooltip>
              )
            ) : hasAccessToPrimary ? (
              <Tooltip key="cloud-virtual" title={t('sites.virtualSiteTooltip')}>
                <IconButton
                  size="small"
                  component={Link}
                  to={primarySitePath}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('sites.virtualSiteTooltip')}
                >
                  <CloudTwoTone fontSize="small" sx={{ color: 'info.main' }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip key="cloud-virtual-no-access" title={t('sites.virtualSiteNoAccessTooltip')}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', p: 0.5 }}>
                  <CloudTwoTone fontSize="small" sx={{ color: 'text.disabled' }} />
                </Box>
              </Tooltip>
            )),
          <Tooltip key="site-settings" title={t('general.settings', 'Settings')}>
            <IconButton
              size="small"
              aria-label={t('general.settings', 'Settings')}
              onClick={() => router.navigate(`${structureDashboardPath}/settings`)}
            >
              <SettingsOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </IconButton>
          </Tooltip>,
          // Delete (deactivate) entry — the dialog itself distinguishes the
          // virtual unshare flow from a real deactivation. md-and-up the
          // action lives in the dashboard's top action bar
          // (SiteDashboardLayout); this icon covers the phone details sheet,
          // where that bar is hidden.
          userPermCheck(user, 'pliny.delete_site') && (
            <Tooltip key="site-delete" title={deleteSiteLabel}>
              <IconButton
                size="small"
                aria-label={deleteSiteLabel}
                onClick={this.openDeleteDialog}
                sx={{ display: { xs: 'inline-flex', md: 'none' } }}
              >
                <DeleteOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          ),
        ]}
      >
        <StructureDetailsList
          customer={customer}
          siteGroup={selectedPlant}
          site={site}
          siteSchema={siteSchema}
          extraFields={extraFields}
          fieldVisibility={fieldVisibility}
          weatherConditions={weatherDisplayConditions}
          weatherFeatureEnabled={weatherFeatureEnabled}
          onUpdateLocation={this.handleUpdateLocation}
        />
        {this.state.deleteOpen && (
          <DeactivateSiteDialog
            open={true}
            closeDialog={this.closeDeleteDialog}
            dispatch={this.props.dispatch}
            router={router}
            unshareVirtualAccess={this.props.unshareVirtualAccess}
            isVirtual={isVirtual}
            site={legacySiteEntity}
            siteGroup={selectedPlant}
            isBillboard={isBillboard}
          />
        )}
      </BasicDashboardCard>
    );
  }
}

const StructureCardContainer = withRouter(connect(mapStateToProps, mapDispatchToProps)(StructureCard));

export default withTranslation()(StructureCardContainer);
